import { type HttpClientInterface, type HttpClientConfig } from '../../infra/http/HttpClient'
import {
  type ChatContact,
  type ChatContactInfo,
  type Conversation,
  type Chat,
  type ConversationParticipant,
  type Contact,
} from '../domain/Chat'
import { mapApiToChat, mapApiToChatContactInfo, mapApiToContact } from './chatMapper'

export type CreateChatContactRequestBody = {
  name: string
  account: {
    name: string
    email: string
    phone?: string
    type: string
  }
  type: string
}

export type CreateConversationRequestBody = {
  participants: Array<{ uuid: string; type?: string }>
  subject?: string
  photo_url?: string
  type: 'direct' | 'group'
  context: {
    source: 'shipper_guide'
    entity_name: 'rate'
    entity_pk: string
  }
}

export type UpdateConversationRequestBody = {
  uuid: string
  participants?: ConversationParticipant[]
  subject?: string
  type: string
}

export type RawChatContext = {
  source: 'shipper_guide'
  entity_name: 'rate'
  entity_pk: string
}

export type RawContact = {
  account: {
    uuid: string
    name: string
    email: string
  }
  type: string | null
}

export type RawChat = {
  uuid: string
  last_message: {
    created_at: string
    id: string
    text: string
    is_unread: boolean
  } | null
  subject: string
  type: string
  participants: ConversationParticipant[]
  context: RawChatContext | null
}

export type RawChatContactInfo = {
  name: string | null
  email: string
  uuid: string
  phone: string | null
  talkjs_token: string
}

type ChatRepositoryDependencies = {
  httpClient: HttpClientInterface
}

export class ChatRepository {
  private httpClient: HttpClientInterface

  constructor({ httpClient }: ChatRepositoryDependencies) {
    this.httpClient = httpClient
  }

  async getChatList(config?: Partial<HttpClientConfig>): Promise<Chat[]> {
    const endpoint = '/conversations'
    const { data } = await this.httpClient.get<RawChat[]>(endpoint, config)
    return data.map(mapApiToChat)
  }

  async createChatContact(
    body: CreateChatContactRequestBody,
    config?: Partial<HttpClientConfig>
  ): Promise<ChatContact> {
    const endpoint = '/contacts'
    const { data } = await this.httpClient.put<ChatContact>(endpoint, body, config)
    return data
  }

  async getContactsList(config?: Partial<HttpClientConfig>): Promise<Contact[]> {
    const endpoint = '/contacts'
    const { data } = await this.httpClient.get<RawContact[]>(endpoint, config)
    return data.map(mapApiToContact)
  }

  async getOrCreateConversation(
    body: CreateConversationRequestBody,
    config?: Partial<HttpClientConfig>
  ): Promise<Conversation> {
    const endpoint = '/conversations'
    const { data } = await this.httpClient.put<Conversation>(endpoint, body, config)
    return data
  }

  async updateConversation(
    body: UpdateConversationRequestBody,
    config?: Partial<HttpClientConfig>
  ): Promise<Conversation> {
    const endpoint = '/conversations'
    const { data } = await this.httpClient.patch<Conversation>(endpoint, body, config)
    return data
  }

  async getUserContactInfo(config?: Partial<HttpClientConfig>): Promise<ChatContactInfo> {
    const endpoint = '/accounts/me'
    const { data } = await this.httpClient.get<RawChatContactInfo>(endpoint, config)
    return mapApiToChatContactInfo(data)
  }
}
